import React from 'react'
import { CardInfoPanel } from '../../../components/ui/CardInfoPanel'
import { PriceHistoryChart } from '../../../components/charts/PriceHistoryChart'
import type { ReportIssueContext } from '../../../components/modals'
import { useCurrency } from '../../../contexts/CurrencyContext'
import { getVariantDirectPriceCents, getVariantPriceCents } from '../../../utils/price'
import {
  AlertCircle,
  Check,
  Flag,
  Loader2,
  Lock,
  Minus,
  Package,
  Plus,
  User,
  X,
  ZoomIn,
} from 'lucide-react'
import type { AddingItemSelection, TradeVariant } from './types'
import { PRINTING_ORDER, STANDARD_CONDITIONS } from './helpers'
import { priceNewCustomerLine } from './customerPricing'
import { priceFollowingCustomerLine, type BulkFloorUsd } from '../../../utils/tradeRules'
import { formatStoreAmount } from '../../Inventories/Inventory/pricing'
import styles from '../CreateTransaction.module.css'

export type AddItemModalProps = {
  addingItem: AddingItemSelection
  formatPrice: (cents: number) => string
  loading: boolean
  itemQuantity: number
  itemValueCents: number
  marketValueCents: number
  overrideTotalValueCents: number | ''
  itemCondition: string
  itemPrinting: string
  selectedVariantId: string
  /** True once the user hand-prices a customer card (locks it from the global %). */
  priceIsOverride: boolean
  /** The store's global "Customer % of Market" default, for helper copy. */
  globalCustomerPercent: number | ''
  /** The store's bulk floor in USD cents (GET /trades/rules) for the preview; the server applies it. */
  bulkFloorUsd?: BulkFloorUsd | null
  onClose: () => void
  onAddToTrade: () => void
  setItemQuantity: React.Dispatch<React.SetStateAction<number>>
  setItemValueCents: React.Dispatch<React.SetStateAction<number>>
  setMarketValueCents: React.Dispatch<React.SetStateAction<number>>
  setOverrideTotalValueCents: React.Dispatch<React.SetStateAction<number | ''>>
  setItemCondition: React.Dispatch<React.SetStateAction<string>>
  setItemPrinting: React.Dispatch<React.SetStateAction<string>>
  setSelectedVariantId: React.Dispatch<React.SetStateAction<string>>
  setPriceIsOverride: React.Dispatch<React.SetStateAction<boolean>>
  onZoomImage: (url: string) => void
  onReportIssue: (context: ReportIssueContext) => void
}

export function AddItemModal({
  addingItem,
  formatPrice,
  loading,
  itemQuantity,
  itemValueCents,
  marketValueCents,
  itemCondition,
  itemPrinting,
  selectedVariantId,
  priceIsOverride,
  globalCustomerPercent,
  bulkFloorUsd = null,
  onClose,
  onAddToTrade,
  setItemQuantity,
  setItemValueCents,
  setMarketValueCents,
  setOverrideTotalValueCents,
  setItemCondition,
  setItemPrinting,
  setSelectedVariantId,
  setPriceIsOverride,
  onZoomImage,
  onReportIssue,
}: AddItemModalProps) {
  const { side, card, product } = addingItem
  const isStoreItem = side === 'store' && Boolean(product)
  const isCustomerItem = side === 'customer'
  const itemName = isStoreItem ? product?.cardName : card?.name
  const itemImage = isStoreItem ? product?.imageUrl : card?.imageUrl
  const maxQty = isStoreItem ? product?.quantity : undefined
  const suggestedPriceCents = isStoreItem ? product?.priceCents : card?.priceCents
  const marketPriceCents = marketValueCents || (typeof suggestedPriceCents === 'number' ? suggestedPriceCents : 0)
  const showMarketPrice = typeof marketPriceCents === 'number' && marketPriceCents > 0
  // On a store item the reference price is what the card is LISTED at (the engine's till figure,
  // sale, floor and markup included), not a market price; it is labelled so, and the card's own
  // market price — when it has one and it differs — is shown beside it.
  const referenceLabel = isStoreItem ? 'listed' : 'market'
  const storeMarketCents = isStoreItem ? product?.marketStoreCents ?? null : null
  const showStoreMarket = storeMarketCents !== null && storeMarketCents > 0 && storeMarketCents !== product?.storePriceCents
  const totalValue = itemQuantity * itemValueCents
  const hasValidValue = itemValueCents > 0

  // All *Cents values flow in the base currency (USD). The market price and totals
  // are converted for display by formatPrice; the editable price input must do the
  // same both ways, otherwise it shows the raw USD number under an "A$" label.
  const { currency, getCurrencySymbol, formatPrice: formatCents, tryConvertPrice, baseCurrency } = useCurrency()
  const displayFormatPrice = formatPrice || formatCents
  const currencyPrefix = getCurrencySymbol(currency)
  const usdToDisplayString = React.useCallback(
    (usdCents: number) => {
      const display = usdCents > 0 ? tryConvertPrice(usdCents, baseCurrency, currency) : null
      return display === null ? '' : (display / 100).toFixed(2)
    },
    [tryConvertPrice, baseCurrency, currency],
  )

  const [localPriceInput, setLocalPriceInput] = React.useState<string>(() => usdToDisplayString(itemValueCents))

  const prevValueCentsRef = React.useRef(itemValueCents)
  React.useEffect(() => {
    if (itemValueCents !== prevValueCentsRef.current) {
      setLocalPriceInput(usdToDisplayString(itemValueCents))
      prevValueCentsRef.current = itemValueCents
    }
  }, [itemValueCents, usdToDisplayString])

  // Local draft for the quantity field so typing stays smooth (a controlled
  // type="number" snapping empty→1 on every keystroke is what made it feel
  // "broken"). Re-sync only when quantity changes from outside this input.
  const [localQtyInput, setLocalQtyInput] = React.useState<string>(String(itemQuantity))
  const prevQtyRef = React.useRef(itemQuantity)
  React.useEffect(() => {
    if (itemQuantity !== prevQtyRef.current) {
      setLocalQtyInput(String(itemQuantity))
      prevQtyRef.current = itemQuantity
    }
  }, [itemQuantity])

  const commitQuantity = (raw: string, { fromBlur }: { fromBlur: boolean }) => {
    let next = parseInt(raw, 10)
    if (!Number.isFinite(next) || next < 1) {
      if (!fromBlur) return // allow a momentarily-empty field while typing
      next = Math.max(1, itemQuantity || 1)
    }
    if (typeof maxQty === 'number' && next > maxQty) next = maxQty
    setLocalQtyInput(String(next))
    setItemQuantity(next)
    prevQtyRef.current = next
  }

  // Write a hand-typed price (entered in the display currency) back to base cents.
  const handlePriceChange = (raw: string) => {
    setLocalPriceInput(raw)
    if (raw === '' || raw === '.') {
      setItemValueCents(0)
      setOverrideTotalValueCents('')
      setPriceIsOverride(true)
      prevValueCentsRef.current = 0
      return
    }
    const value = parseFloat(raw) || 0
    const displayCents = Math.max(0, Math.round(value * 100))
    const usdCents = tryConvertPrice(displayCents, currency, baseCurrency)
    if (usdCents === null) return // no rate yet: keep the previous value rather than guess
    setItemValueCents(usdCents)
    setOverrideTotalValueCents('')
    setPriceIsOverride(true)
    prevValueCentsRef.current = usdCents
  }

  const useMarketPrice = () => {
    setItemValueCents(marketPriceCents)
    setOverrideTotalValueCents('')
    setPriceIsOverride(true)
  }

  // Customer-only: hand the card back to the global "Customer % of Market".
  const followGlobal = () => {
    setPriceIsOverride(false)
    setOverrideTotalValueCents('')
    if (marketPriceCents > 0) {
      setItemValueCents(typeof globalCustomerPercent === 'number'
        ? priceFollowingCustomerLine(marketPriceCents, globalCustomerPercent, bulkFloorUsd).unitValueCents
        : marketPriceCents)
    }
  }

  const globalPercentLabel = typeof globalCustomerPercent === 'number' ? `${globalCustomerPercent}%` : null
  // The % this card will be added at: the one typed here, else the global % it follows.
  const followsGlobalPercent = isCustomerItem && !priceIsOverride && typeof globalCustomerPercent === 'number'
  // A card following the trade's % under the store's bulk floor is worth the floor's flat amount.
  const followPreview = followsGlobalPercent && marketPriceCents > 0
    ? priceFollowingCustomerLine(marketPriceCents, globalCustomerPercent, bulkFloorUsd)
    : null
  const [showTrend, setShowTrend] = React.useState(false) // the chart only loads when opened

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={`${styles.modalHeader} ${isCustomerItem ? styles.customerItemHeader : styles.storeItemHeader}`}>
          <div className={styles.modalHeaderContent}>
            {isCustomerItem ? <User size={20} /> : <Package size={20} />}
            <h3>Add to {side === 'store' ? "store's" : "customer's"} cards</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.cardPreviewRow}>
            <CardInfoPanel
              variant="compact"
              name={itemName || 'Unknown Item'}
              imageUrl={itemImage || undefined}
              setName={isStoreItem ? product?.setName : card?.setName}
              setNumber={!isStoreItem ? card?.setNumber : undefined}
              gameName={!isStoreItem ? card?.gameName : undefined}
              printing={isStoreItem ? product?.printing : undefined}
              condition={isStoreItem ? product?.condition : undefined}
            />
            {!isStoreItem && card && (
              <button
                type="button"
                className={styles.reportBtnSmall}
                onClick={() => {
                  onReportIssue({
                    cardName: card.name,
                    cardId: card.id,
                    setName: card.setName,
                    setCode: card.setCode,
                    setId: card.setId,
                    setNumber: card.setNumber,
                    gameName: card.gameName,
                    rarity: card.rarity || undefined,
                    printing: itemPrinting || undefined,
                    condition: itemCondition || undefined,
                    priceCents: marketPriceCents || undefined,
                    source: 'Trade',
                  })
                }}
                title="Report an issue with this card"
                aria-label="Report an issue with this card"
              >
                <Flag size={14} aria-hidden />
              </button>
            )}
            {itemImage && (
              <button
                type="button"
                className={styles.zoomBtnSmall}
                onClick={() => onZoomImage(itemImage)}
                title="Zoom card image"
                aria-label="Zoom card image"
              >
                <ZoomIn size={14} aria-hidden />
              </button>
            )}
          </div>

          {/* Variant / condition + printing (customer cards only) */}
          {!isStoreItem && card && card.variants && card.variants.length > 0 ? (
            <div className={styles.field}>
              <label>Variant</label>
              <select
                value={selectedVariantId}
                onChange={(e) => {
                  const variantId = e.target.value
                  setSelectedVariantId(variantId)
                  setPriceIsOverride(false)

                  const variant = card.variants?.find((v) => v.id === variantId) as TradeVariant | undefined
                  if (variant) {
                    setItemCondition(variant.condition || '')
                    setItemPrinting(variant.printing || '')
                    const market = getVariantPriceCents(variant)
                    const line = getVariantDirectPriceCents(variant)
                    // A customer card follows the global % (the variant change unlocked it above).
                    setItemValueCents(
                      isCustomerItem
                        ? priceNewCustomerLine({
                          suggestedValueCents: line > 0 ? line : 0,
                          marketValueCents: market,
                          globalPercent: typeof globalCustomerPercent === 'number' ? globalCustomerPercent : null,
                          floor: bulkFloorUsd,
                        }).valueCents
                        : line > 0 ? line : 0,
                    )
                    setMarketValueCents(market)
                    setOverrideTotalValueCents('')
                  }
                }}
                className={styles.variantSelect}
                aria-label="Card variant"
              >
                {card.variants.map((variant) => {
                  const v = variant as TradeVariant
                  const price = getVariantPriceCents(v)
                  let priceLabel: string
                  if (price > 0) {
                    priceLabel = displayFormatPrice(price)
                  } else if (v.estimatedPriceCents && v.estimatedPriceCents > 0) {
                    priceLabel = `~${displayFormatPrice(v.estimatedPriceCents)} (est.)`
                  } else {
                    priceLabel = 'Manual price required'
                  }
                  return (
                    <option key={v.id} value={v.id}>
                      {v.printing || 'Standard'} · {v.condition || 'Ungraded'} · {priceLabel}
                    </option>
                  )
                })}
              </select>
              {(() => {
                const selVariant = card.variants?.find((v) => v.id === selectedVariantId) as TradeVariant | undefined
                if (selVariant && getVariantPriceCents(selVariant) <= 0) {
                  return (
                    <div className={styles.validationWarning} style={{ marginTop: '0.5rem' }}>
                      <AlertCircle size={16} aria-hidden />
                      <span>
                        No market price for this variant.
                        {selVariant.estimatedPriceCents && selVariant.estimatedPriceCents > 0
                          ? ` Estimated ~${displayFormatPrice(selVariant.estimatedPriceCents)} from the Near Mint price.`
                          : ''}{' '}
                        Enter a value manually below.
                      </span>
                    </div>
                  )
                }
                return null
              })()}
            </div>
          ) : !isStoreItem ? (
            <div className={styles.pricingRow}>
              <div className={styles.field}>
                <label>Condition</label>
                <select value={itemCondition} onChange={(e) => setItemCondition(e.target.value)} aria-label="Condition">
                  <option value="">Select condition...</option>
                  {STANDARD_CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>Printing</label>
                <select value={itemPrinting} onChange={(e) => setItemPrinting(e.target.value)} aria-label="Printing">
                  <option value="">Select printing...</option>
                  {Object.keys(PRINTING_ORDER)
                    .filter((p, i, arr) => arr.indexOf(p) === i)
                    .sort((a, b) => (PRINTING_ORDER[a] ?? 99) - (PRINTING_ORDER[b] ?? 99))
                    .map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          ) : null}

          {/* Pricing — quantity, price, and % of market grouped together */}
          <div className={styles.pricingCard}>
            <div className={styles.pricingCardHead}>
              <span className={styles.pricingTitle}>Pricing</span>
              {showMarketPrice && (
                <span className={styles.marketChip}>
                  {isStoreItem ? 'Listed' : 'Market'}{' '}
                  <strong>
                    {isStoreItem && typeof product?.storePriceCents === 'number' && product.storeCurrency
                      ? formatStoreAmount(formatCents, product.storePriceCents, product.storeCurrency, displayFormatPrice(marketPriceCents))
                      : displayFormatPrice(marketPriceCents)}
                  </strong>
                </span>
              )}
              {showStoreMarket && product?.storeCurrency && (
                <span className={styles.marketChip}>
                  Market <strong>{formatStoreAmount(formatCents, storeMarketCents, product.storeCurrency, '—')}</strong>
                </span>
              )}
            </div>

            <div className={styles.pricingRow}>
              <div className={styles.field}>
                <label>
                  Quantity
                  {typeof maxQty === 'number' && <span className={styles.labelHint}>Max {maxQty}</span>}
                </label>
                <div className={styles.quantityInput}>
                  <button
                    type="button"
                    onClick={() => setItemQuantity(Math.max(1, itemQuantity - 1))}
                    disabled={itemQuantity <= 1}
                    aria-label="Decrease quantity"
                  >
                    <Minus size={16} aria-hidden />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={localQtyInput}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '')
                      if (digits === '') {
                        setLocalQtyInput('')
                        return
                      }
                      commitQuantity(digits, { fromBlur: false })
                    }}
                    onBlur={() => commitQuantity(localQtyInput, { fromBlur: true })}
                    aria-label="Quantity"
                  />
                  <button
                    type="button"
                    onClick={() => setItemQuantity(maxQty ? Math.min(maxQty, itemQuantity + 1) : itemQuantity + 1)}
                    disabled={typeof maxQty === 'number' && itemQuantity >= maxQty}
                    aria-label="Increase quantity"
                  >
                    <Plus size={16} aria-hidden />
                  </button>
                </div>
              </div>

              <div className={styles.field}>
                <label>
                  {isCustomerItem ? 'Your offer (each)' : 'Your price (each)'}
                  {!hasValidValue && <span className={styles.labelError}>Required</span>}
                </label>
                <div className={`${styles.priceInput} ${!hasValidValue ? styles.priceInputError : ''}`}>
                  <span className={styles.priceInputSymbol}>{currencyPrefix}</span>
                  <input
                    type="number"
                    value={localPriceInput}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    onBlur={() => {
                      const v = parseFloat(localPriceInput)
                      setLocalPriceInput(
                        localPriceInput.trim() && Number.isFinite(v) ? v.toFixed(2) : usdToDisplayString(itemValueCents),
                      )
                    }}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    autoFocus={!hasValidValue}
                    aria-label={`Price per item in ${currency}`}
                  />
                  {showMarketPrice && itemValueCents !== marketPriceCents && (
                    <button type="button" onClick={useMarketPrice} className={styles.priceUseMarketBtn}>
                      Use {referenceLabel}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* One % for the whole trade; a card's own price is the only per-card choice. */}
            {isCustomerItem && (
              <p className={styles.overrideHelp}>
                {priceIsOverride ? (
                  <>
                    <Lock size={11} aria-hidden /> Your price. The trade&rsquo;s % won&rsquo;t change this card.{' '}
                    <button type="button" className={styles.followGlobalBtn} onClick={followGlobal}>
                      Follow the trade instead
                    </button>
                  </>
                ) : followPreview ? (
                  <>
                    {globalPercentLabel} of market{followPreview.floorApplied ? ' (bulk floor)' : ''}. Change the price to set this card by hand.
                  </>
                ) : (
                  <>Full market value. Change the price to set this card by hand.</>
                )}
              </p>
            )}
          </div>

          {isCustomerItem && card && (
            <details className={styles.priceTrend} onToggle={(e) => setShowTrend(e.currentTarget.open)}>
              <summary>
                Price trend
                <span>{itemPrinting || 'Any printing'} · {itemCondition || 'Any condition'}</span>
              </summary>
              {showTrend && (
                <PriceHistoryChart
                  cardId={card.id}
                  days={90}
                  title="Card market history"
                  printing={itemPrinting || undefined}
                  condition={itemCondition || undefined}
                  chartHeight={180}
                />
              )}
            </details>
          )}

          {!hasValidValue && (
            <div className={styles.validationWarning}>
              <AlertCircle size={16} aria-hidden />
              <span>Enter a price greater than {currencyPrefix}0.00 to continue.</span>
            </div>
          )}
        </div>

        <div className={`${styles.modalActions} ${styles.addItemActions}`}>
          <div className={styles.addItemTotal}>
            <span>Total{itemQuantity > 1 ? ` · ${itemQuantity} × ${displayFormatPrice(itemValueCents)}` : ''}</span>
            <b>{displayFormatPrice(totalValue)}</b>
          </div>
          <button className={styles.cancelBtn} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={`${styles.confirmBtn} ${!hasValidValue ? styles.confirmBtnDisabled : ''}`}
            onClick={onAddToTrade}
            disabled={loading || !hasValidValue}
            type="button"
          >
            {loading ? (
              <>
                <Loader2 className={styles.spinner} size={16} aria-hidden />
                Adding...
              </>
            ) : (
              <>
                <Check size={16} aria-hidden />
                Add to Trade
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
